import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { MyAuditApi } from '../tokens/my-audit.token';
import { AuditEvent, MyAuditFilter, MyAuditPage } from '../models/audit-event.model';

/**
 * REST-only implementation of `MyAuditApi` (EM-309).
 *
 * Calls:
 *   GET /services/embedded/api/me/audit/events            (cursor-paginated list)
 *   GET /services/embedded/api/me/audit/events/{id}       (single event)
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Rule 07 / Rule 10 —
 * no Mock fallback; Playwright specs intercept via `page.route()`. Rule 09 — the raw
 * `HttpErrorResponse` is propagated for the caller to surface via `extractBackendErrorMessage`.
 *
 * Holds the accumulated list + cursor + loading flags as signals so the Dashboard feed
 * (Story 7) and the My Audit Log screen (Story 9) can react without re-fetching.
 */
@Injectable()
export class MyAuditRestService implements MyAuditApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_PATHS.ME_AUDIT}/events`;

  private readonly eventsSig = signal<AuditEvent[]>([]);
  private readonly loadingSig = signal<boolean>(false);
  private readonly hasMoreSig = signal<boolean>(false);
  private nextCursor: number | null = null;
  private lastFilter: MyAuditFilter = {};
  private lastSize = 20;

  readonly events = this.eventsSig.asReadonly();
  readonly loading = this.loadingSig.asReadonly();
  readonly hasMore = this.hasMoreSig.asReadonly();

  load(filter: MyAuditFilter = {}, size = 20): Observable<MyAuditPage> {
    this.lastFilter = filter;
    this.lastSize = size;
    this.loadingSig.set(true);
    return this.http
      .get<MyAuditPage>(this.base, { params: this.buildParams(filter, undefined, size) })
      .pipe(
        tap((page) => {
          this.eventsSig.set(page.events);
          this.nextCursor = page.nextCursor;
          this.hasMoreSig.set(page.nextCursor != null);
          this.loadingSig.set(false);
        }),
      );
  }

  loadMore(): Observable<MyAuditPage> {
    if (this.nextCursor == null) {
      return of({ events: [], nextCursor: null });
    }
    this.loadingSig.set(true);
    return this.http
      .get<MyAuditPage>(this.base, {
        params: this.buildParams(this.lastFilter, this.nextCursor, this.lastSize),
      })
      .pipe(
        tap((page) => {
          this.eventsSig.update((current) => [...current, ...page.events]);
          this.nextCursor = page.nextCursor;
          this.hasMoreSig.set(page.nextCursor != null);
          this.loadingSig.set(false);
        }),
      );
  }

  latest(size: number): Observable<AuditEvent[]> {
    return this.http
      .get<MyAuditPage>(this.base, { params: this.buildParams({}, undefined, size) })
      .pipe(map((page) => page.events));
  }

  getById(id: number): Observable<AuditEvent> {
    return this.http.get<AuditEvent>(`${this.base}/${id}`);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  private buildParams(filter: MyAuditFilter, cursor: number | undefined, size: number): HttpParams {
    let p = new HttpParams().set('size', String(size));
    if (filter.type) {
      p = p.set('type', filter.type);
    }
    if (filter.from) {
      p = p.set('from', filter.from);
    }
    if (filter.to) {
      p = p.set('to', filter.to);
    }
    if (cursor != null) {
      p = p.set('cursor', String(cursor));
    }
    return p;
  }
}
